import { Box, Button, Stack, SvgIcon, useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Alert, Snackbar, Typography, SelectChangeEvent } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Download01, ArrowUp } from '../../assets';
import StoresTab from './components/StoresTab';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import { UserContext } from '../../components/shared/useUser';
import { useNavigate } from 'react-router-dom';
import { client, Instance, Market, MarketStoreUploadRequest, MarketStoreUploadResult } from '../../app/services/api/orderManagementClient';
import useLogError from '../../hooks/useLogError';

const StoresPage = () => {
  const { t } = useTranslation('stores');
  const theme = useTheme();
  const navigate = useNavigate();
  const { logError } = useLogError();

  const { hasPermissionTo } = useContext(UserContext);
  const [hasPermissionToCreateStore, setHasPermissionToCreateStore] = useState<boolean>(false);

  const [currentFilterInstance, setCurrentFilterInstance] = useState<string>('');
  const [currentFilterMarket, setCurrentFilterMarket] = useState<string>('');

  // Download/Upload dialog states
  const [showDownloadDialog, setShowDownloadDialog] = useState<boolean>(false);
  const [showUploadDialog, setShowUploadDialog] = useState<boolean>(false);
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const [instances, setInstances] = useState<Instance[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<MarketStoreUploadResult | null>(null);
  
  // Snackbar states
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');

  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  useEffect(() => {
    setHasPermissionToCreateStore(hasPermissionTo(['StoreCreate', 'ConfigurationAdmin']));
    loadInstances();
  }, [hasPermissionTo]);

  useEffect(() => {
    if (selectedInstance) {
      loadMarkets(selectedInstance);
    } else {
      setMarkets([]);
      setSelectedMarket('');
    }
  }, [selectedInstance]);

  const loadInstances = async () => {
    try {
      const response = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setInstances(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load instances');
    }
  };

  const loadMarkets = async (instanceId: string) => {
    try {
      const response = await client.getMarkets({
        companyId: instanceId,
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setMarkets(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load markets');
    }
  };

  const handleCreateNew = () => {
    navigate('/stores/create');
  };

  const onCreateNew = () => {
    handleCreateNew();
  };

  const handleFilterChange = (instance: string, market: string) => {
    setCurrentFilterInstance(instance);
    setCurrentFilterMarket(market);
  };

  const handleDownloadClick = () => {
    if (currentFilterInstance && currentFilterMarket) {
      setMessage(t('downloadStarting'));
      setIsSuccess(true);
      setShowMessage(true);
      handleDownload(currentFilterInstance, currentFilterMarket);
    } else {
      setSelectedInstance('');
      setSelectedMarket('');
      setShowDownloadDialog(true);
    }
  };

  const handleUploadClick = () => {
    setSelectedInstance('');
    setSelectedMarket('');
    setUploadFile(null);
    setUploadResult(null);
    setShowUploadDialog(true);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    setSelectedInstance(event.target.value);
    setSelectedMarket('');
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    setSelectedMarket(event.target.value);
  };

  const handleDownload = async (instanceId?: string, marketId?: string) => {
    const downloadInstance = instanceId || selectedInstance;
    const downloadMarket = marketId || selectedMarket;
    
    if (!downloadInstance || !downloadMarket) {
      setMessage('Please select both Instance and Market to download stores');
      setIsSuccess(false);
      setShowMessage(true);
      return;
    }

    setIsDownloading(true);
    
    if (!instanceId && !marketId) {
      setMessage(t('downloadStarting'));
      setIsSuccess(true);
      setShowMessage(true);
    }
    
    try {
      const response = await client.downloadMarketStores(downloadInstance, downloadMarket);
      
      if (response.success && response.data) {
        // Create blob and download
        const byteCharacters = atob(response.data.fileData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.fileName || 'stores.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setMessage(t('downloadSuccessMessage'));
        setIsSuccess(true);
        setShowMessage(true);
        setShowDownloadDialog(false);
      } else {
        setMessage(response.message || t('downloadErrorMessage'));
        setIsSuccess(false);
        setShowMessage(true);
      }
    } catch (error) {
      logError('Failed to download stores');
      setMessage(t('downloadErrorMessage'));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadFile(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !selectedInstance || !selectedMarket) {
      setMessage('Please select a file and ensure Instance and Market are selected');
      setIsSuccess(false);
      setShowMessage(true);
      return;
    }

    setIsUploading(true);
    try {
      // Convert file to base64
      const fileBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]); // Remove data:application/...;base64, prefix
        };
        reader.onerror = reject;
        reader.readAsDataURL(uploadFile);
      });

      const uploadRequest: MarketStoreUploadRequest = {
        companyId: parseInt(selectedInstance),
        customerTypeId: parseInt(selectedMarket),
        fileBase64: fileBase64,
      };

      const response = await client.uploadMarketStores(uploadRequest);
      
      if (response.success && response.data) {
        setUploadResult(response.data);
        setMessage(t('uploadSuccessMessage'));
        setIsSuccess(true);
        setShowMessage(true);
        // Don't close dialog yet, show results
      } else {
        setMessage(response.message || t('uploadErrorMessage'));
        setIsSuccess(false);
        setShowMessage(true);
      }
    } catch (error) {
      logError('Failed to upload stores');
      setMessage(t('uploadErrorMessage'));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const canViewStores = hasPermissionTo(['StoreView', 'ConfigurationAdmin']);
  const canExportStores = hasPermissionTo(['StoreExport', 'ConfigurationAdmin']);
  const canImportStores = hasPermissionTo(['StoreImport', 'ConfigurationAdmin']);

  if (!canViewStores) {
    return null;
  }

  return (
    <PageArea>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={4}
        sx={{
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 4,
        }}
      >
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle subTitle={t('subtitle')}>{t('stores')}</PageTitle>
        </Box>
        <Box
          width={isMobile ? '100%' : 'auto'}
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            gap: 2,
          }}
        >
          {canExportStores && (
            <Button
              variant="secondary"
              size="lg"
              startIcon={<SvgIcon><Download01 /></SvgIcon>}
              onClick={handleDownloadClick}
              sx={{
                mb: { xs: 4, md: 0 },
              }}
            >
              {t('download')}
            </Button>
          )}
          {canImportStores && (
            <Button
              variant="secondary"
              size="lg"
              startIcon={<SvgIcon><ArrowUp /></SvgIcon>}
              onClick={handleUploadClick}
              sx={{
                mb: { xs: 4, md: 0 },
              }}
            >
              {t('upload')}
            </Button>
          )}
          {hasPermissionToCreateStore && (
            <Button
              variant="primary"
              size="lg"
              startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}
              onClick={handleCreateNew}
              sx={{
                mb: { xs: 4, md: 0 },
              }}
            >
              {t('createNew')}
            </Button>
          )}
        </Box>
      </Stack>
      
      <StoresTab 
        onCreateNew={onCreateNew} 
        onFilterChange={handleFilterChange} 
      />

      {/* Download Dialog */}
      <Dialog
        open={showDownloadDialog}
        onClose={() => setShowDownloadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('download')} {t('stores')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel shrink>
                {t('filterByInstance')}
              </InputLabel>
              <Select
                value={selectedInstance}
                onChange={handleInstanceChange}
                label={t('filterByInstance')}
                displayEmpty
                notched
              >
                <MenuItem value="">
                  {t('selectInstance')}
                </MenuItem>
                {instances.map((instance) => (
                  <MenuItem 
                    key={instance.id} 
                    value={instance.id.toString()}
                  >
                    {instance.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel shrink>
                {t('filterByMarket')}
              </InputLabel>
              <Select
                value={selectedMarket}
                onChange={handleMarketChange}
                label={t('filterByMarket')}
                displayEmpty
                notched
                disabled={!selectedInstance}
              >
                <MenuItem value="">
                  {t('selectMarket')}
                </MenuItem>
                {markets.map((market) => (
                  <MenuItem 
                    key={market.customerTypeId} 
                    value={market.customerTypeId.toString()}
                  >
                    {market.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDownloadDialog(false)}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={() => handleDownload()} 
            variant="primary"
            disabled={!selectedInstance || !selectedMarket || isDownloading}
          >
            {isDownloading ? t('downloading') : t('download')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('upload')} {t('stores')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel shrink>
                {t('filterByInstance')}
              </InputLabel>
              <Select
                value={selectedInstance}
                onChange={handleInstanceChange}
                label={t('filterByInstance')}
                displayEmpty
                notched
              >
                <MenuItem value="">
                  {t('selectInstance')}
                </MenuItem>
                {instances.map((instance) => (
                  <MenuItem 
                    key={instance.id} 
                    value={instance.id.toString()}
                  >
                    {instance.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel shrink>
                {t('filterByMarket')}
              </InputLabel>
              <Select
                value={selectedMarket}
                onChange={handleMarketChange}
                label={t('filterByMarket')}
                displayEmpty
                notched
                disabled={!selectedInstance}
              >
                <MenuItem value="">
                  {t('selectMarket')}
                </MenuItem>
                {markets.map((market) => (
                  <MenuItem 
                    key={market.customerTypeId} 
                    value={market.customerTypeId.toString()}
                  >
                    {market.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Typography sx={{ mb: 2 }}>
                {t('uploadInstructions')}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <input
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  id="upload-file"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="upload-file">
                  <Button 
                    component="span"
                    sx={{ mr: 2 }}>
                    {t('chooseFile')}
                  </Button>
                </label>
                <Typography component="span">
                  {uploadFile ? uploadFile.name : t('noFileSelected')}
                </Typography>
              </Box>
            </Box>
            {uploadResult && (
              <Box sx={{ mt: 2 }}>
                <Typography>{t('uploadResults')}</Typography>
                <Typography>{t('created')}: {uploadResult.created}</Typography>
                <Typography>{t('updated')}: {uploadResult.updated}</Typography>
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography color="error">{t('errors')}:</Typography>
                    {uploadResult.errors.map((error, index) => (
                      <Typography 
                        key={index} 
                        color="error"
                      >
                        {error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUploadDialog(false)}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleUpload} 
            variant="primary"
            disabled={!uploadFile || !selectedInstance || !selectedMarket || isUploading}
          >
            {isUploading ? t('uploading') : t('upload')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Message */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default StoresPage;
